import { useCallback, useState, useEffect } from 'react';

import { Icon } from './Icon';

async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const buffer = await crypto.subtle.digest('SHA-256', encoder.encode(password));
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export type PasswordGateProps = {
  /** SHA-256 hex hash of the expected password. Generate with: echo -n 'password' | shasum -a 256 */
  expectedHash: string;
  storageKey: string;
  children: React.ReactNode;
};

export function PasswordGate({ expectedHash, storageKey, children }: PasswordGateProps) {
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // Check stored hash on mount
  useEffect(() => {
    (async () => {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        if (stored === expectedHash) {
          setAuthenticated(true);
        } else {
          localStorage.removeItem(storageKey);
        }
      }
      setChecking(false);
    })();
  }, [storageKey, expectedHash]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');

      const hash = await hashPassword(password);

      if (hash === expectedHash) {
        localStorage.setItem(storageKey, hash);
        setAuthenticated(true);
      } else {
        setError('Incorrect password');
      }
    },
    [password, storageKey, expectedHash],
  );

  // If no hash is configured, disable the gate
  if (!expectedHash) return <>{children}</>;
  if (checking) return null;
  if (authenticated) return <>{children}</>;

  return (
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-black/55 p-4 backdrop-blur-[2px]"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="password-gate-title"
        className="w-full max-w-sm rounded-xl border border-border bg-background p-6 shadow-lg"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon name="streamline:interface-lock-circle-circle-frame-key-keyhole-lock-locked-secure-security" className="h-6 w-6" />
          </div>
          <div className="text-center">
            <h2
              id="password-gate-title"
              className="text-lg font-semibold text-foreground"
            >
              Access Required
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Enter the password to access this environment.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="w-full space-y-3">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                placeholder="Password"
                autoFocus
                className="w-full rounded-lg border border-border bg-background px-4 py-2.5 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? (
                  <Icon name="streamline:interface-edit-view-off-disable-eye-eyeball-hide-off-view" className="h-4 w-4" />
                ) : (
                  <Icon name="streamline:interface-edit-view-eye-eyeball-open-view" className="h-4 w-4" />
                )}
              </button>
            </div>

            {error && (
              <p className="text-center text-sm font-medium text-ascend-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={!password}
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
            >
              Continue
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
